/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import { generateUrl } from '../../routes/util';
import { AccessType, MetricGroup } from '../../types/metricGroup';
import { RouteInterface } from '../../types/routes';
import { CardGridButtonProps, CardGridItemProps } from '../survey-scope/CardGrid';
import { getCardIcon } from '../survey-scope/CustomMetricCards';
import { QUESTION } from '../../constants/terminology';

export interface CustomMetricCardProps extends CardGridItemProps {
  accessType?: AccessType;
}

export interface MetricGroupsProps {
  initiativeId: string;
  baseRoute: RouteInterface;
  metricGroups: MetricGroup[];
  handleDeleteMetricGroup: (groupId: string) => void;
  handleCreateMetricGroup: () => void;
  handleDuplicateMetricGroup: (groupId: string) => void;
}

export const getCustomMetricGroups = ({
  initiativeId,
  baseRoute,
  metricGroups,
  handleCreateMetricGroup,
  handleDeleteMetricGroup,
  handleDuplicateMetricGroup,
}: MetricGroupsProps) => {
  const getButtons: (metricGroup: MetricGroup) => CardGridButtonProps[] = (metricGroup) => {
    const isAssigned = metricGroup.accessType === AccessType.Assigned;
    const buttons = [];

    if (!isAssigned) {
      buttons.push({
        tooltip: metricGroup.groupName ? `Duplicate ${metricGroup.groupName}` : undefined,
        button: (
          <Button
            outline
            onClick={() => handleDuplicateMetricGroup(metricGroup._id)}
            className='icon-button card-grid-button'
          >
            <i className='fal fa-copy'></i>
          </Button>
        ),
      });
    }

    buttons.push({
      icon: <i className='fal fa-cog' />,
      tooltip: `${isAssigned ? 'View' : 'Edit'} Custom Metric`,
      to: `${generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId, groupId: metricGroup._id })}`,
    });

    if (!isAssigned) {
      buttons.push({
        tooltip: metricGroup.groupName ? `Delete ${metricGroup.groupName} from Custom Metrics` : undefined,
        button: (
          <Button
            outline
            onClick={() => handleDeleteMetricGroup(metricGroup._id)}
            className='icon-button card-grid-button'
            color='danger'
          >
            <i className='fal fa-trash-can'></i>
          </Button>
        ),
      });
    }

    return buttons;
  };

  const cards: CustomMetricCardProps[] = metricGroups.map((metricGroup: MetricGroup) => {
    const card: CustomMetricCardProps = {
      key: `scope-cardgriditem-metricgroup-${metricGroup._id}`,
      title: <span>{metricGroup.groupName}</span>,
      sortTitle: metricGroup.groupName,
      description: metricGroup.description ?? '',
      icon: getCardIcon(metricGroup),
      unitCount: metricGroup.universalTrackers?.length ?? 0,
      unitName: QUESTION.CAPITALIZED_PLURAL,
      buttons: getButtons(metricGroup),
      scopeTag: metricGroup._id,
      inScope: false,
      isPartial: false,
      accessType: metricGroup.accessType ?? AccessType.Custom,
    };
    return card;
  });

  // Add create new pack button to the first group
  cards.unshift({
    key: 'scope-cardgriditem-metricgroup-createnew',
    title: <span>Create a new survey pack</span>,
    sortTitle: '000000 Create a new survey pack',
    subtitle: 'Click here if you would like to create a new survey pack of custom metrics',
    icon: (
      <span className='new-survey-pack-icon' onClick={handleCreateMetricGroup}>
        <i className='fa fa-plus' />
      </span>
    ),
    unitCount: undefined,
    unitName: '',
    buttons: [
      {
        icon: <span className='px-3'>Create</span>,
        tooltip: 'Create metric question groups',
        to: `${generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId, groupId: 'create' })}`,
      },
    ],
    scopeTag: '',
    inScope: false,
    isPartial: false,
    accessType: AccessType.Custom,
  });

  return cards;
};
